import type { ValidationPreview } from 'shared/odata/types.ts'

import { useStore } from '@nanostores/react'
import restrictImage from 'assets/image/snail-no.png'
import thinkingImage from 'assets/image/snail-typing.png'
import clsx from 'clsx'
import { type Campaign, campaignHooks } from 'features/campaigns/index.ts'
import { atom } from 'nanostores'
import { Fragment } from 'react'
import { modal } from 'shared/styles/index.ts'
import { ControlledDialog } from 'shared/ui/Dialog/ControlledDialog.tsx'
import { Button, LoadingButton, StackIcon } from 'shared/ui/index.ts'

import css from './ConfirmationDialogRunning.module.scss'

interface State {
	body: Campaign.AudienceParams
	onSuccess?: () => void
	validation: ValidationPreview
}

const $state = atom<null | State>(null)
const closeModal = () => $state.set(null)

ConfirmationDialogRunning.open = $state.set

const NegativeCase = ({
	summary
}: { summary: ValidationPreview['validation_rules_summary'] }) => (
	<>
		<h1 className={css.title}>
			Sorry, the system can&apos;t add <br />
			these contacts
		</h1>

		<dl className={css.list}>
			{summary.map((item, index) => (
				<Fragment key={`${item.count}_${item.description}_${index}`}>
					<dt className={css.count}>{`${item.count} contacts`}</dt>

					<dd className={css.dd}>{item.description}</dd>
				</Fragment>
			))}
		</dl>

		<p className={css.text}>Please select other contacts</p>

		<Button className={css.confirm} onClick={closeModal}>
			Ok
		</Button>
	</>
)

export function ConfirmationDialogRunning() {
	const data = useStore($state)

	const isWrongRequest =
		data &&
		data.validation.validation_rules_summary.length &&
		!data.validation.valid_audience_count

	return (
		<ControlledDialog
			className={clsx(
				css.wrapper,
				isWrongRequest ? css.isNegative : css.isDefault
			)}
			onRequestClose={closeModal}
			open={data !== null}
			rootClassName={css.root}
		>
			<button className={modal.close} onClick={closeModal} type='button'>
				<StackIcon icon='close' stroke='currentcolor' />
			</button>
			{isWrongRequest && (
				<NegativeCase summary={data.validation.validation_rules_summary} />
			)}
			{data && !isWrongRequest && <RequestFlow {...data} />}

			<img
				alt='decorative image'
				aria-hidden
				className={clsx(
					css.decorative,
					isWrongRequest && css.negativeDecoration
				)}
				src={isWrongRequest ? restrictImage : thinkingImage}
				width={135}
			/>
		</ControlledDialog>
	)
}

function RequestFlow({ body, onSuccess, validation }: State) {
	const [addContacts, addContactsProps] = campaignHooks.useAddContacts()

	const isPositiveAudienceCount =
		validation.validation_rules_summary.length &&
		validation.valid_audience_count

	let totalCount = 0

	return (
		isPositiveAudienceCount && (
			<>
				<h1 className={css.title}>
					Sorry, the system can&apos;t add
					<br /> some of the contacts
				</h1>
				<dl className={css.list}>
					{validation.validation_rules_summary.map((item, index) => {
						totalCount += item.count

						return (
							<Fragment key={`${item.count}_${item.description}_${index}`}>
								<dt className={css.count}>{`${item.count} contacts`}</dt>

								<dd className={css.dd}>{item.description}</dd>
							</Fragment>
						)
					})}
				</dl>
				<p className={clsx(css.text, css.textUp)}>
					The system will add {validation.valid_audience_count} contacts, <br />
					<span className={css.red}>{totalCount} will not be added.</span>
					Would you like to proceed?
				</p>
				<div className={css.actions}>
					<Button
						className={css.action}
						onClick={closeModal}
						variant='bordered-dark'
					>
						Cancel
					</Button>
					<LoadingButton
						className={css.action}
						isLoading={addContactsProps.isLoading}
						onClick={async () => {
							await addContacts(body)
							onSuccess?.()
							closeModal()
						}}
					>
						Confirm
					</LoadingButton>
				</div>
			</>
		)
	)
}
